export enum AccountStatus {
	OPENED = 'OPENED',
	CLOSED = 'CLOSED',
	BLOCKED = 'BLOCKED',
	DEBIT_BLOCKED = 'DEBIT_BLOCKED',
	WAITING_FOR_APPROVAL = 'WAITING_FOR_APPROVAL',
	CREDIT_BLOCKED = 'CREDIT_BLOCKED',
	REJECTED = 'REJECTED',
}

export enum BatchStatus {
	INITIALIZING = 'INITIALIZING',
	UNSIGNED = 'UNSIGNED',
	SIGNING = 'SIGNING',
	SIGNED = 'SIGNED',
	FAILED = 'FAILED',
	EXECUTED = 'EXECUTED',
	CREATED = 'CREATED',
	UNSIGNED_WITH_WARNINGS = 'UNSIGNED_WITH_WARNINGS',
	EXECUTED_WITH_WARNINGS = 'EXECUTED_WITH_WARNINGS',
}

export enum OperationStatus {
	CREATED = 'created',
	SIGNED = 'signed',
	PROCESSED = 'processed',
	FAILED = 'failed',
	EXECUTED = 'executed',
}

export enum PAYMENT_TYPE {
	DOMESTIC = 'DOMESTIC',
	INTERNAL = 'INTERNAL',
	FX = 'FX',
	INTERNATIONAL = 'INTERNATIONAL',
	BATCH = 'batch',
}

export enum FxRemittanceType {
	LOCAL = 'local',
	SWIFT = 'swift',
}

export interface Payer {
	name: string;
	iban: string;
}

export interface FXQuote {
	id: string;
	amount_from: number;
	amount_to: number;
	amount_from_formatted: string;
	amount_to_formatted: string;
	currency_from: string;
	currency_to: FXCurrencies;
	customer_rate: string;
	expires_at: string;
}

export interface Address {
	country: string;
	building_number?: string;
	line?: string;
	postal_code?: string;
	town: string;
	country_sub_division?: string;
	building_name?: string;
	street_name?: string;
}

export interface BankAddress {
	country: string;
	line: string;
	town: string;
	postal_code: string;
	building_number: string;
}

export interface Bank {
	name: string;
	bic: string;
	address: BankAddress;
}

export interface Beneficiary {
	name: string;
	account_number: string;
	address: Address;
	bank_routing_number: string;
	bank: Bank;
}

export interface AvailableCurrency {
	iso_code: string;
	name: string;
	remittance_type: FxRemittanceType;
}

export interface FXFormFieldOption {
	code?: string;
	name?: string;
	value?: string;
	label?: string;
}

export interface FXFormFieldConfig {
	type: 'string' | 'number' | 'select';
	label: string;
	order?: number;
	length?: [number, number];
	required: boolean;
	placeholder?: string;
	value?: string;
	hidden?: boolean;
	options?: (string | FXFormFieldOption)[];
	disabled?: boolean;
	format?: string;
}

export interface FXFormSectionConfig {
	fields: Record<string, FXFormFieldConfig>;
	disabled?: boolean;
}

export interface FXCurrencyConfig {
	general: FXFormSectionConfig;
	local: FXFormSectionConfig;
	swift: FXFormSectionConfig;
}

export interface FXCurrencyFormConfigEntry {
	iso_code: string;
	name: string;
	config: FXCurrencyConfig;
}

export enum FXCurrencies {
	AUD = 'AUD',
	CAD = 'CAD',
	CHF = 'CHF',
	CZK = 'CZK',
	DKK = 'DKK',
	GBP = 'GBP',
	HUF = 'HUF',
	ILS = 'ILS',
	NOK = 'NOK',
	NZD = 'NZD',
	PLN = 'PLN',
	RON = 'RON',
	SEK = 'SEK',
	SGD = 'SGD',
	USD = 'USD',
}

export interface ClassifierResponse {
	availableCurrencies: AvailableCurrency[];
	fxCurrencyFormConfig: Record<string, FXCurrencyFormConfigEntry>;
}

export interface FXPayment {
	id: string;
	status: OperationStatus;
	paymentType: PAYMENT_TYPE.FX;
	payment_type: PAYMENT_TYPE.FX;
	amount: number;
	currency: string;
	account_uuid: string;
	account: string;
	payer: Payer;
	execution_date?: string | null;
	created_at: string;
	settled_at?: string | null;
	processing_started_at?: string | null;
	payment_urgency: 'NORMAL';
	payment_details: string;
	payment_details_type: 'ADVA';
	document_number: string;
	beneficiary: Beneficiary;
	group: unknown | null;
	signatures: unknown[];
	ultimate_beneficiary: unknown | null;
	fx_quote: FXQuote;
	fx_remittance_type: FxRemittanceType;
	correspondent: unknown | null;
}

export type FXPaymentForm = Omit<
	FXPayment,
	| 'id'
	| 'status'
	| 'paymentType'
	| 'created_at'
	| 'settled_at'
	| 'processing_started_at'
	| 'execution_date'
	| 'signatures'
>;
